import PizZip from "pizzip";
import Docxtemplater from "docxtemplater";
import ImageModule from "docxtemplater-image-module-free";

// Transparent 1x1 PNG base64
const TRANSPARENT_IMAGE =
  "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR42mP8Xw8AApMBgUhzDdEAAAAASUVORK5CYII=";

const DOC_PROTECTION_PASSWORD =
  process.env.REACT_APP_DOC_PROTECTION_PASSWORD || "";
const DOC_PROTECTION_SPIN_COUNT = 100000;

function base64ToArrayBuffer(base64) {
  try {
    const binaryString = window.atob(base64.split(",")[1] || base64);
    const len = binaryString.length;
    const bytes = new Uint8Array(len);
    for (let i = 0; i < len; i++) {
      bytes[i] = binaryString.charCodeAt(i);
    }
    return bytes.buffer;
  } catch (e) {
    console.error("Invalid base64 string passed:", base64, e);
    return new ArrayBuffer(0);
  }
}

function getScreenshotDataUrl(screenshot) {
  if (typeof screenshot === "string") {
    if (screenshot.startsWith("data:")) return screenshot;
    return `data:image/png;base64,${screenshot}`;
  }

  if (screenshot?.base64) {
    if (screenshot.base64.startsWith("data:")) return screenshot.base64;
    return `data:${screenshot.content_type || "image/png"};base64,${
      screenshot.base64
    }`;
  }

  return TRANSPARENT_IMAGE;
}

export function flattenConfigValues(obj) {
  const result = {};

  function recurse(current) {
    if (typeof current !== "object" || current === null) return;

    for (const key in current) {
      const value = current[key];

      // If value is a final value (string, number, boolean)
      if (typeof value !== "object" || value === null || Array.isArray(value)) {
        // Ignore empty strings and "-" if needed
        if (value !== "") {
          // Put into result
          result[key] =
            value &&
            value
              .toString()
              .split("_")
              .map((word) => word.charAt(0).toUpperCase() + word.substring(1))
              .join(" ");
        }
      } else {
        // Recurse deeper
        recurse(value);
      }
    }
  }

  recurse(obj);
  return result;
}

function formatNum(number) {
  return number !== undefined
    ? number.toLocaleString(
        undefined, // leave undefined to use the visitor's browser
        // locale or a string like 'en-US' to override it.
        { minimumFractionDigits: 2 },
      )
    : "-";
}

function flattenCost(data) {
  const result = {};

  for (const topLvlKey in data) {
    const value = data[topLvlKey];

    // If value is an object, merge its children into the result
    if (typeof value === "object" && !Array.isArray(value)) {
      if (value["NRE"]) {
        result[topLvlKey + " REC"] = "$" + formatNum(value["REC"]);
        result[topLvlKey + " NRE"] = "$" + formatNum(value["NRE"]);
        result[topLvlKey + " Lead"] = value["Lead"] + " Week/s";
      } else
        for (const subKey in value) {
          result[subKey + " REC"] = "$" + formatNum(value[subKey]["REC"]);
          result[subKey + " NRE"] = "$" + formatNum(value[subKey]["NRE"]);
          result[subKey + " Lead"] = value[subKey]["Lead"] + " Week/s";
        }
    } else {
      // If it's not an object (unlikely in your input), keep as-is
      result[topLvlKey + " REC"] = "$" + formatNum(value[topLvlKey]["REC"]);
      result[topLvlKey + " NRE"] = "$" + formatNum(value[topLvlKey]["NRE"]);
      result[topLvlKey + " Lead"] = value[topLvlKey]["Lead"] + " Week/s";
    }
  }

  return result;
}

function bytesToBase64(bytes) {
  let binary = "";
  bytes.forEach((byte) => {
    binary += String.fromCharCode(byte);
  });
  return window.btoa(binary);
}

function stringToUtf16LeBytes(value) {
  const bytes = new Uint8Array(value.length * 2);

  for (let i = 0; i < value.length; i++) {
    const code = value.charCodeAt(i);
    bytes[i * 2] = code & 0xff;
    bytes[i * 2 + 1] = code >> 8;
  }

  return bytes;
}

function concatBytes(...arrays) {
  const length = arrays.reduce((total, array) => total + array.length, 0);
  const merged = new Uint8Array(length);
  let offset = 0;

  arrays.forEach((array) => {
    merged.set(array, offset);
    offset += array.length;
  });

  return merged;
}

function getSpinCountBytes(value) {
  const bytes = new Uint8Array(4);
  const view = new DataView(bytes.buffer);
  view.setUint32(0, value, true);
  return bytes;
}

async function hashBytes(bytes) {
  const hash = await window.crypto.subtle.digest("SHA-512", bytes);
  return new Uint8Array(hash);
}

async function createProtectionAttributes(password) {
  const salt = window.crypto.getRandomValues(new Uint8Array(16));
  const passwordBytes = stringToUtf16LeBytes(password);
  let hash = await hashBytes(concatBytes(salt, passwordBytes));

  for (let i = 0; i < DOC_PROTECTION_SPIN_COUNT; i++) {
    hash = await hashBytes(concatBytes(hash, getSpinCountBytes(i)));
  }

  return [
    'w:algorithmName="SHA-512"',
    `w:hashValue="${bytesToBase64(hash)}"`,
    `w:saltValue="${bytesToBase64(salt)}"`,
    `w:spinCount="${DOC_PROTECTION_SPIN_COUNT}"`,
    'w:cryptProviderType="rsaAES"',
    'w:cryptAlgorithmClass="hash"',
    'w:cryptAlgorithmType="typeAny"',
    'w:cryptAlgorithmSid="14"',
    `w:cryptSpinCount="${DOC_PROTECTION_SPIN_COUNT}"`,
    `w:hash="${bytesToBase64(hash)}"`,
    `w:salt="${bytesToBase64(salt)}"`,
  ].join(" ");
}

async function applyReadOnlyProtection(zip) {
  if (!DOC_PROTECTION_PASSWORD) {
    throw new Error(
      "Missing REACT_APP_DOC_PROTECTION_PASSWORD. Set it before starting or building the app to enforce DOCX protection with a password.",
    );
  }

  const settingsPath = "word/settings.xml";
  const settings = zip.file(settingsPath)?.asText();
  if (!settings) return;

  const passwordAttributes = ` ${await createProtectionAttributes(
    DOC_PROTECTION_PASSWORD,
  )}`;
  const protectionTag = `<w:documentProtection w:edit="readOnly" w:enforcement="1"${passwordAttributes}/>`;

  const nextSettings = settings.includes("<w:documentProtection")
    ? settings.replace(/<w:documentProtection\b[^>]*\/>/, protectionTag)
    : settings.replace("</w:settings>", `${protectionTag}</w:settings>`);

  zip.file(settingsPath, nextSettings);
}

export async function generateDoc(data) {
  let renderObj = {
    name: data?.name || "",
    date: data?.date || "",
    product: data?.product || "",
    productBodyType: data?.productBodyType || "",
    companyName: data?.companyName || "",
    config: data?.config,
  };

  if (data?.config) {
    renderObj = {
      ...renderObj,
      ...flattenConfigValues(data?.config),
      ...flattenCost(data?.config?.cost),
    };
  }

  (data?.screenshots || []).forEach((screenshot, i) => {
    renderObj[`image${i}`] = getScreenshotDataUrl(screenshot);
  });

  for (let i = 0; i < 4; i++) {
    if (!renderObj[`image${i}`]) renderObj[`image${i}`] = TRANSPARENT_IMAGE;
  }

  const templateUrl = "/template.docx";
  const response = await fetch(templateUrl);
  if (!response.ok) {
    throw new Error(
      `Failed to load DOCX template from ${templateUrl}: ${response.status} ${response.statusText}`,
    );
  }

  const arrayBuffer = await response.arrayBuffer();
  const signature = String.fromCharCode(
    ...new Uint8Array(arrayBuffer.slice(0, 2)),
  );
  if (signature !== "PK") {
    throw new Error(
      `Invalid DOCX template loaded from ${templateUrl}. Expected a .docx zip file, but received ${response.headers.get("content-type") || "unknown content type"}.`,
    );
  }

  const zip = new PizZip(arrayBuffer);
  await applyReadOnlyProtection(zip);

  // Use ImageModule properly
  const imageModule = new ImageModule({
    getImage: (tagValue) => base64ToArrayBuffer(tagValue),
    getSize: () => [300, 300],
  });

  const doc = new Docxtemplater(zip, {
    modules: [imageModule],
    nullGetter: () => "",
  });
  await doc.renderAsync(renderObj);

  const docxBlob = doc.getZip().generate({
    type: "blob",
    mimeType:
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  });

  return docxBlob; // for preview
}
